// import { createWithEqualityFn } from "zustand/traditional"; // causing problem with testing
import { StateStorage, createJSONStorage, devtools, persist, subscribeWithSelector } from "zustand/middleware";

import { logger } from "./middlewares";
import { createSelectors } from "./utils";

import { StateCreator, create } from "zustand";

import { isDevelopment } from "../utils";
import { createAuthStore, type TAuthStore } from "./auth";
import { createChatSlice, type TChatStore } from "./chat/createChatSlice";
import { createCompaniesSlice, type TCompaniesStore } from "./companies";
import { createDashboardSlice, type TDashboardStore } from "./dashboard";
import { createDataGridSlice, type TDataGridStore } from "./dataGrid";
import { createGuidanceSlice, type TGuidanceStore } from "./guidance";
import { createOnboardingSlice, type TOnboardingStore } from "./onboarding";

export type TStore = TAuthStore &
  TChatStore &
  TCompaniesStore &
  TDashboardStore &
  TOnboardingStore &
  TGuidanceStore &
  TDataGridStore;

// NOTE: must keep unique keys for each store slice
export const store: StateCreator<TStore> = (...args) => ({
  ...createAuthStore(...args),
  ...createChatSlice(...args),
  ...createCompaniesSlice(...args),
  ...createDashboardSlice(...args),
  ...createOnboardingSlice(...args),
  ...createGuidanceSlice(...args),
  ...createDataGridSlice(...args),
});

const isDebugMode = isDevelopment && Boolean(import.meta.env.VITE_VITEST_ENV) === false;

export const STORE_VERSION = 1;
export const STORE_NAME = "appStore";
export const useStoreBase = create<TStore>()(
  logger(
    devtools(
      // to subscribe with a selector, now useStore.subscribe accepts addtional signature
      // subscribe(selector, callback, options?: { equalityFn, fireImmediately }): Unsubscribe
      subscribeWithSelector(
        // READ-MORE: https://docs.pmnd.rs/zustand/integrations/persisting-store-data
        persist(store, {
          // required, (unique)
          name: STORE_NAME,
          // (optional) by default the 'localStorage' is used. Custom storage function(implementing StateStorage type) can also be used.
          storage: createJSONStorage(() => sessionStorage as StateStorage),
          // old persisted version of the store will be dumped and new persisted store will be used if version doesn't match.
          // NOTE: use migrate function to handle breaking changes in order to persist previously stored data.
          version: STORE_VERSION,
          // enables us to pick specific state's fields to be stored in the storage
          // Exclude Sets from persistence (transient state and Set can't be serialized)
          partialize: (state) => {
            const {
              loadingSections,
              loadingCompanyIds,
              isLoading, // companies loading state
              isProfileLoading, // auth profile loading state
              isUpdatingProfile, // auth update profile loading state
              isRefreshing, // auth token refresh state
              // Verification OTP (transient)
              isSendingVerificationOtp,
              isResendingVerificationOtp,
              isVerifyingVerificationOtp,
              verificationOtpMethod,
              verificationOtpSentAt,
              verificationOtpExpiresAt,
              verificationOtpResendAvailableAt,
              verificationOtpFailedAttempts,
              // Guidance uses localStorage keyed by user ID — not sessionStorage
              guidanceUserId,
              guidanceState,
              activeTourId,
              isGuidanceHydrated,
              // Data grid prefs use their own localStorage bucket — not sessionStorage
              gridPreferences,
              isDataGridHydrated,
              ...rest
            } = state;
            return rest;
          },
          migrate: (persistedState, prevVersion) => {
            if (prevVersion !== STORE_VERSION) {
              // handle breaking changes here
              // persistedState.newField = persistedState.oldField
              // delete persistedState.oldField
            }

            // Always clean up transient loading states from persisted state
            // These should never be restored from storage - they must start fresh
            if (persistedState && typeof persistedState === "object") {
              const {
                loadingSections,
                loadingCompanyIds,
                isLoading,
                isProfileLoading,
                isUpdatingProfile,
                isRefreshing,
                isSendingVerificationOtp,
                isResendingVerificationOtp,
                isVerifyingVerificationOtp,
                verificationOtpMethod,
                verificationOtpSentAt,
                verificationOtpExpiresAt,
                verificationOtpResendAvailableAt,
                verificationOtpFailedAttempts,
                guidanceUserId,
                guidanceState,
                activeTourId,
                isGuidanceHydrated,
                gridPreferences,
                isDataGridHydrated,
                ...rest
              } = persistedState as {
                loadingSections?: unknown;
                loadingCompanyIds?: unknown;
                isLoading?: unknown;
                isProfileLoading?: unknown;
                isUpdatingProfile?: unknown;
                isRefreshing?: unknown;
                isSendingVerificationOtp?: unknown;
                isResendingVerificationOtp?: unknown;
                isVerifyingVerificationOtp?: unknown;
                verificationOtpMethod?: unknown;
                verificationOtpSentAt?: unknown;
                verificationOtpExpiresAt?: unknown;
                verificationOtpResendAvailableAt?: unknown;
                verificationOtpFailedAttempts?: unknown;
                guidanceUserId?: unknown;
                guidanceState?: unknown;
                activeTourId?: unknown;
                isGuidanceHydrated?: unknown;
                gridPreferences?: unknown;
                isDataGridHydrated?: unknown;
                [key: string]: unknown;
              };
              return rest;
            }

            return persistedState;
          },
        })
      ),
      { enabled: isDebugMode }
    ),
    {
      name: STORE_NAME,
      enabled: isDebugMode,
    }
  )
);

export const clearStore = () => useStoreBase.persist.clearStorage();

export const useStore = createSelectors(useStoreBase);
